/**
 * Parse a timestamp coming from the database into a correct Date.
 *
 * Supabase `timestamp without time zone` columns (e.g. orders.created_at) are
 * stored in UTC but returned WITHOUT any timezone marker, like
 * "2026-02-15 10:00:00" or "2026-02-15T10:00:00". JavaScript's `new Date()`
 * treats such strings as LOCAL time, which shifts every timestamp by the
 * viewer's UTC offset (e.g. +5:30 in India) and makes fresh orders look hours
 * old. We fix this by explicitly treating timezone-less datetimes as UTC, so
 * the moment is absolute and each browser renders it in its own local zone.
 */
export function parseServerDate(date: string | Date | null): Date | null {
  if (!date) return null;
  if (date instanceof Date) return date;

  const trimmed = date.trim();
  // Already has timezone info (Z or ±hh:mm) — parse as-is.
  const hasTimezone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(trimmed);
  // Only datetimes (contain a time component) need the UTC hint; plain dates
  // like "2026-02-15" are already parsed as UTC midnight by JS.
  const hasTime = trimmed.includes(':');

  let normalized = trimmed;
  if (hasTime && !hasTimezone) {
    normalized = trimmed.replace(' ', 'T') + 'Z';
  }

  const parsed = new Date(normalized);
  return isNaN(parsed.getTime()) ? new Date(date) : parsed;
}

/**
 * Format a date as a human-friendly relative time
 * @param date - Date to format (string, Date, or null)
 * @returns Human-friendly relative time string
 */
export function getRelativeTime(date: string | Date | null): string {
  if (!date) return 'Not started';
  
  const now = new Date();
  const targetDate = parseServerDate(date) as Date;
  const diffInSeconds = Math.floor((now.getTime() - targetDate.getTime()) / 1000);
  
  // Future dates
  if (diffInSeconds < 0) {
    const absDiff = Math.abs(diffInSeconds);
    
    if (absDiff < 60) return 'In a moment';
    if (absDiff < 3600) {
      const mins = Math.floor(absDiff / 60);
      return `In ${mins} ${mins === 1 ? 'minute' : 'minutes'}`;
    }
    if (absDiff < 86400) {
      const hours = Math.floor(absDiff / 3600);
      return `In ${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    }
    if (absDiff < 172800) return 'Tomorrow';
    
    const days = Math.floor(absDiff / 86400);
    return `In ${days} days`;
  }
  
  // Past dates
  if (diffInSeconds < 10) return 'Just now';
  if (diffInSeconds < 60) return `${diffInSeconds} seconds ago`;
  if (diffInSeconds < 3600) {
    const mins = Math.floor(diffInSeconds / 60);
    return `${mins} ${mins === 1 ? 'minute' : 'minutes'} ago`;
  }
  if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  }
  if (diffInSeconds < 172800) return 'Yesterday';
  if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} days ago`;
  }
  if (diffInSeconds < 2592000) {
    const weeks = Math.floor(diffInSeconds / 604800);
    return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
  }
  
  const months = Math.floor(diffInSeconds / 2592000);
  return `${months} ${months === 1 ? 'month' : 'months'} ago`;
}

/**
 * Format ETA in a compact, human-friendly format
 * @param etaDate - Estimated completion date (string, Date, or null)
 * @returns Compact ETA string like "Ready in 5h 42m", "Ready Tomorrow", etc.
 */
export function getCompactETA(etaDate: string | Date | null): string {
  if (!etaDate) return 'TBD';
  
  const now = new Date();
  const targetDate = parseServerDate(etaDate) as Date;
  const diffInSeconds = Math.floor((targetDate.getTime() - now.getTime()) / 1000);
  
  // Past dates
  if (diffInSeconds < 0) return 'Overdue';
  
  // Less than 1 hour
  if (diffInSeconds < 3600) {
    const mins = Math.floor(diffInSeconds / 60);
    return `Ready in ${mins}m`;
  }
  
  // Less than 24 hours
  if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    const mins = Math.floor((diffInSeconds % 3600) / 60);
    return mins > 0 ? `Ready in ${hours}h ${mins}m` : `Ready in ${hours}h`;
  }
  
  // Tomorrow
  if (diffInSeconds < 172800) {
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    
    const targetMidnight = new Date(targetDate);
    targetMidnight.setHours(0, 0, 0, 0);
    
    if (tomorrow.getTime() === targetMidnight.getTime()) {
      return 'Ready Tomorrow';
    }
  }
  
  // Multiple days
  const days = Math.floor(diffInSeconds / 86400);
  return `Ready in ${days} ${days === 1 ? 'Day' : 'Days'}`;
}

/**
 * Format a date as a full datetime string for tooltips
 * @param date - Date to format
 * @returns Formatted string like "Apr 22, 2026, 4:30 PM"
 */
export function getFullDateTime(date: string | Date | null): string {
  if (!date) return 'Not available';
  
  const targetDate = parseServerDate(date) as Date;
  
  return targetDate.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Get the most recent activity timestamp from an order
 * @param order - Order object with various timestamp fields
 * @returns Most recent timestamp
 */
export function getMostRecentActivity(order: {
  created_at?: string | Date | null;
  updated_at?: string | Date | null;
  assigned_at?: string | Date | null;
  editing_started_at?: string | Date | null;
  qc_started_at?: string | Date | null;
  completed_at?: string | Date | null;
}): Date | null {
  const timestamps = [
    order.completed_at,
    order.qc_started_at,
    order.editing_started_at,
    order.assigned_at,
    order.updated_at,
    order.created_at,
  ].filter(Boolean).map(t => parseServerDate(t as string) as Date);
  
  if (timestamps.length === 0) return null;
  
  return new Date(Math.max(...timestamps.map(d => d.getTime())));
}

/**
 * Format a short date for display
 * @param date - Date to format
 * @returns Short date string like "Apr 22"
 */
export function getShortDate(date: string | Date | null): string {
  if (!date) return 'TBD';
  
  const targetDate = parseServerDate(date) as Date;
  
  return targetDate.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Calculate completion time from created date to completion
 * @param createdAt - Order creation date
 * @param completedAt - Order completion date
 * @returns Duration string like "3h 42m" or "2 days"
 */
export function getCompletionDuration(createdAt: string | Date, completedAt: string | Date | null): string {
  if (!completedAt) return 'In progress';
  
  const start = parseServerDate(createdAt) as Date;
  const end = parseServerDate(completedAt) as Date;
  
  const diffInSeconds = Math.floor((end.getTime() - start.getTime()) / 1000);
  
  if (diffInSeconds < 3600) {
    const mins = Math.floor(diffInSeconds / 60);
    return `${mins}m`;
  }
  
  if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    const mins = Math.floor((diffInSeconds % 3600) / 60);
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  
  const days = Math.floor(diffInSeconds / 86400);
  if (days === 1) return '1 day';
  
  return `${days} days`;
}

/**
 * Format delivery information for completed orders
 * @param completedAt - Completion timestamp
 * @returns Delivery string like "Delivered on Jun 7 at 4:31 PM"
 */
export function getDeliveryInfo(completedAt: string | Date | null): string {
  if (!completedAt) return 'Not completed';
  
  const date = parseServerDate(completedAt) as Date;
  
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).replace(',', ' at');
}
